import { z } from 'zod';
import { TypeOfAPIError, TypeOfAPIResponse } from '@ninja/api-client';
import { APIFactoryOptions, factory } from '../../../config';

const pagingAndSortingSchema = z.object({
	paging: z.object({
		pageSize: z.number(),
		pageNumber: z.number(),
	}),
	sorting: z.object({
		columnName: z.string(),
		sortOrder: z.union([z.literal(1), z.literal(2)]),
	}),
});

const requestSchema = z
	.object({
		recordIds: z.array(z.number()).optional(),
		quoteReferenceNo: z.string().optional(),
		quoteListNo: z.string().optional(),
		sponsorName: z.string().optional(),
		sponsorEid: z.string().optional(),
		customerEmailAddress: z.string().optional(),
		customerMobileNo: z.string().optional(),
		policyTypeId: z.number().optional(),
		sourceId: z.number().optional(),
		quoteGenerationDateFrom: z.string().optional(),
		quoteGenerationDateTo: z.string().optional(),
		quoteExpiryDateFrom: z.string().optional(),
		quoteExpiryDateTo: z.string().optional(),
		policyNumber: z.string().optional(),
		insuranceCampanyId: z.number().optional(),
		planId: z.number().optional(),
		policyEffectiveDateTo: z.string().optional(),
		policyEffectiveDateFrom: z.string().optional(),
		policyExpiryDateFrom: z.string().optional(),
		policyExpiryDateTo: z.string().optional(),
		quoteStatusId: z.number().optional(),
		isCustomerSignUp: z.boolean().optional(),
		planTypeId: z.number().optional(),
		planEmirateId: z.number().optional(),
	})
	.merge(pagingAndSortingSchema);

const responseRecordSchema = z.object({
	recordId: z.string(),
	policyId: z.string(),
	quoteListNo: z.string(),
	customerMobile: z.string(),
	sponsorName: z.string(),
	membersCount: z.number(),
	quoteGenerationDate: z.string(),
	quoteExpiryDate: z.string(),
	planName: z.string(),
	planType: z.string(),
	channelTypeId: z.number(),
	planEmirate: z.string(),
	policyType: z.string(),
	policyEffectiveDate: z.string(),
	policyExpiryDate: z.string(),
	insuranceCompanyName: z.string(),
	policyAmountExcludingVat: z.number(),
	policyAmountIncludingVat: z.number(),
	totalBasamaFees: z.number(),
	vatOnTotalBasamaFees: z.number(),
	icpFeesExclVAT: z.number(),
	vatAmountOnICPFees: z.number(),
	dohFine: z.number(),
	quoteStatus: z.string(),
	quoteReferenceNumber: z.string(),
	customerId: z.string(),
	policyNumber: z.string(),
	icAdditionalFeesExcludingVAT: z.number(),
	vatOnICAdditionalFees: z.number(),
	bmiLoading: z.number().nullish(),
	vatOnBMILoading: z.number().nullish(),
	vatPercentage: z.number().nullish(),
	vatAmount: z.number().nullish(),
});

const responseSchema = z.object({
	data: z.array(responseRecordSchema),
	totalItemsCount: z.number(),
});

const postGeneratedQuotationsReport = (options: APIFactoryOptions) =>
	factory(options).createAPIDefinition({
		url: '/report/generated-quotes',
		method: 'POST',
		responseSchema,
		requestSchema,
		transformResponse: (response) => ({
			data: response.data.report,
			totalItemsCount: response.data.paging.totalRecords,
		}),
	});

export type PostGeneratedQuotationsReportRequest = z.infer<typeof requestSchema>;

export type PostGeneratedQuotationsReportResponse = TypeOfAPIResponse<ReturnType<typeof postGeneratedQuotationsReport>>;

export type PostGeneratedQuotationsReportRecord = z.infer<typeof responseRecordSchema>;

export type PostGeneratedQuotationsReportError = TypeOfAPIError<ReturnType<typeof postGeneratedQuotationsReport>>;

export default postGeneratedQuotationsReport;
